import React, { useState, useEffect } from 'react';
import { brand } from '../../config/brand';
import {
  Settings as SettingsIcon,
  Type,
  Palette,
  Save,
  Reply,
  Loader2,
  Lock,
  Eye,
  EyeOff
} from 'lucide-react';
import ColorPickerWithAlpha from '../../components/ColorPickerWithAlpha';
import { useAppConfig, AppConfig } from '../../hooks/useAppConfig';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import { useTheme } from '../../components/theme-provider';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "../../components/ui/card";
import { toast } from "sonner";
import axios from 'axios';
import { API_BASE_URL } from '../../config';

export default function Settings(): JSX.Element {
  const { theme, setTheme } = useTheme();
  const [config, updateConfig] = useAppConfig();
  const { localStorage: globalState, updateLocalStorage: updateGlobalState, user, setUser } = useGlobalState();
  const [localConfig, setLocalConfig] = useState<AppConfig>(config);
  const [autoReplyUsername, setAutoReplyUsername] = useState<boolean>(user?.autoReplyUsername || false);
  const [isSavingPrefs, setIsSavingPrefs] = useState(false);

  // Change password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const isOAuthOnly = user?.googleId && !user?.hasPassword;

  const handleChangePassword = async () => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setIsChangingPassword(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/users/change-password`,
        { currentPassword: isOAuthOnly ? undefined : currentPassword, newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to change password');
    } finally {
      setIsChangingPassword(false);
    }
  };

  useEffect(() => {
    setLocalConfig(config);
  }, [config]);

  useEffect(() => {
    if (user) {
      setAutoReplyUsername(user.autoReplyUsername || false);
    }
  }, [user?.autoReplyUsername]);

  const handleSave = async () => {
    updateConfig(localConfig);
    
    // Sync with global state so it affects the uploader immediately
    updateGlobalState({
      debugMode: localConfig.debugMode
    });

    // Save user preferences
    if (user && (autoReplyUsername !== user.autoReplyUsername)) {
      setIsSavingPrefs(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.patch(
          `${API_BASE_URL}/users/preferences`,
          { autoReplyUsername },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setUser({
          ...user,
          autoReplyUsername: response.data.autoReplyUsername
        });
      } catch (err) {
        console.error("Error saving user preferences:", err);
        toast.error("Failed to save user preferences");
      } finally {
        setIsSavingPrefs(false);
      }
    }
    
    toast.success("Settings saved successfully!");
  };

  return (
    <div className="container mx-auto py-10 px-4 max-w-4xl space-y-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="bg-primary/10 p-3 rounded-2xl border border-primary/20 shadow-lg shadow-primary/5">
            <SettingsIcon className="h-8 w-8 text-primary" />
          </div>
          <div>
            <h1 className="text-4xl font-black text-foreground tracking-tight italic uppercase">Settings</h1>
            <p className="text-muted-foreground font-medium">Customize your experience and preferences.</p>
          </div>
        </div>
      </div>

      <div className="grid gap-8">
        {/* Appearance Section */}
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center gap-2 text-primary mb-1">
              <Palette className="h-5 w-5" />
              <CardTitle>Appearance</CardTitle>
            </div>
            <CardDescription className="text-muted-foreground">
              Customize how {brand.name} looks on your screen.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-3 max-w-sm">
              <Label className="text-foreground">Theme Mode</Label>
              <Select value={theme} onValueChange={(v: "dark" | "olive") => setTheme(v)}>
                <SelectTrigger className="bg-background border-border text-foreground">
                  <SelectValue placeholder="Select theme" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border text-foreground">
                  <SelectItem value="dark">Dark</SelectItem>
                  <SelectItem value="olive">Olive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Caption Style Defaults Section */}
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center gap-2 text-primary mb-1">
              <Type className="h-5 w-5" />
              <CardTitle>Caption Style Defaults</CardTitle>
            </div>
            <CardDescription className="text-muted-foreground">
              Default appearance for your video captions.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="grid gap-3">
                <Label className="text-foreground">Font Family</Label>
                <Select 
                  value={localConfig.captionDefaults.fontFamily} 
                  onValueChange={(v: string) => setLocalConfig({ 
                    ...localConfig, 
                    captionDefaults: { ...localConfig.captionDefaults, fontFamily: v }
                  })}
                >
                  <SelectTrigger className="bg-background border-border text-foreground">
                    <SelectValue placeholder="Select Font" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border text-foreground">
                    {['Arial', 'Helvetica', 'Times New Roman', 'Courier New', 'Verdana', 'Georgia', 'Impact', 'Comic Sans MS'].map(f => (
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-3">
                <Label className="text-foreground">Font Size (px)</Label>
                <Input 
                  type="number" 
                  className="bg-background border-border text-foreground"
                  min={12} 
                  max={100}
                  value={localConfig.captionDefaults.fontSize}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocalConfig({ 
                    ...localConfig, 
                    captionDefaults: { ...localConfig.captionDefaults, fontSize: parseInt(e.target.value) || 24 }
                  })}
                />
              </div>

              <div className="grid gap-3">
                <Label className="text-foreground">Text Color</Label>
                <ColorPickerWithAlpha
                  label="Text Color"
                  value={localConfig.captionDefaults.color}
                  onChange={(value: string) => setLocalConfig({ 
                    ...localConfig, 
                    captionDefaults: { ...localConfig.captionDefaults, color: value }
                  })}
                  disableAlpha={true}
                />
              </div>

              <div className="grid gap-3">
                <Label className="text-foreground">Background Color</Label>
                <ColorPickerWithAlpha
                  label="Background Color"
                  value={localConfig.captionDefaults.backgroundColor}
                  onChange={(value: string) => setLocalConfig({ 
                    ...localConfig, 
                    captionDefaults: { ...localConfig.captionDefaults, backgroundColor: value }
                  })}
                />
              </div>
            </div>

            <div className="grid gap-3">
              <Label className="text-foreground">Default Position</Label>
              <Select 
                value={localConfig.captionDefaults.position} 
                onValueChange={(v: 'top' | 'center' | 'bottom') => setLocalConfig({ 
                  ...localConfig, 
                  captionDefaults: { ...localConfig.captionDefaults, position: v }
                })}
              >
                <SelectTrigger className="bg-background border-border text-foreground">
                  <SelectValue placeholder="Select Position" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border text-foreground">
                  <SelectItem value="top">Top</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="bottom">Bottom</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
          <CardFooter className="flex justify-end pt-2">
            <Button 
              onClick={handleSave}
              className="bg-primary hover:bg-primary/90 text-white font-bold px-8 py-6 rounded-lg"
            >
              <Save className="mr-2 h-5 w-5" />
              Save All Changes
            </Button>
          </CardFooter>
        </Card>

        {/* Social Features Section */}
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center gap-2 text-primary mb-1">
              <Reply className="h-5 w-5" />
              <CardTitle>Social Features</CardTitle>
            </div>
            <CardDescription className="text-muted-foreground">
              Configure how you interact with social media.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-background/50 rounded-lg border border-border">
              <div className="space-y-0.5">
                <Label className="text-foreground text-base">Auto-add @username in replies</Label>
                <p className="text-sm text-muted-foreground">Include the handle of the person you're replying to in the text box.</p>
              </div>
              <Switch 
                checked={autoReplyUsername}
                onCheckedChange={(checked: boolean) => setAutoReplyUsername(checked)}
              />
            </div>

          </CardContent>
          <CardFooter className="flex justify-end pt-2">
            <Button 
              onClick={handleSave}
              disabled={isSavingPrefs}
              className="bg-primary hover:bg-primary/90 text-white font-bold px-8 py-6 rounded-lg"
            >
              {isSavingPrefs ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-5 w-5" />
                  Save Social Settings
                </>
              )}
            </Button>
          </CardFooter>
        </Card>

        {/* Change Password Section */}
        <Card className="bg-card border-border">
          <CardHeader>
            <div className="flex items-center gap-2 text-primary mb-1">
              <Lock className="h-5 w-5" />
              <CardTitle>Change Password</CardTitle>
            </div>
            <CardDescription className="text-muted-foreground">
              {isOAuthOnly
                ? 'You signed in with Google. Set a password to also enable email/password login.'
                : 'Update your account password.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!isOAuthOnly && (
              <div className="grid gap-2 max-w-sm">
                <Label className="text-foreground">Current Password</Label>
                <div className="relative">
                  <Input
                    type={showCurrentPassword ? 'text' : 'password'}
                    className="bg-background border-border text-foreground pr-10"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            )}
            <div className="grid gap-2 max-w-sm">
              <Label className="text-foreground">New Password</Label>
              <div className="relative">
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  className="bg-background border-border text-foreground pr-10"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="grid gap-2 max-w-sm">
              <Label className="text-foreground">Confirm New Password</Label>
              <Input
                type="password"
                className="bg-background border-border text-foreground"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat new password"
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-500">Passwords do not match</p>
              )}
            </div>
          </CardContent>
          <CardFooter className="flex justify-end pt-2">
            <Button
              onClick={handleChangePassword}
              disabled={isChangingPassword || !newPassword || newPassword !== confirmPassword || (!isOAuthOnly && !currentPassword)}
              className="bg-primary hover:bg-primary/90 text-white font-bold px-8 py-6 rounded-lg"
            >
              {isChangingPassword ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Changing...
                </>
              ) : (
                <>
                  <Lock className="mr-2 h-5 w-5" />
                  {isOAuthOnly ? 'Set Password' : 'Change Password'}
                </>
              )}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
